import { Component, Inject, Input, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router'; 

import { combineLatest, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

import { UserService } from '../../../data-domains/humain/user/services/user.service';
import { UserTLR } from '../../../data-domains/humain/user/entities/user-tlr.entity';
import { EventBusService, Events } from '../../../technical-modules/events/services/event-bus.service';

type MenuEntry = {
	path: string,
	title: string,
	icon: string,
	label: string,
};

@Component({
	selector: 'navigation-menu',
	templateUrl: 'navigation-menu.html',
	styleUrls: ['navigation-menu.css'],
	// Desactivation de l'encapsulation, pour eviter un bug preventant le styling du Material Menu
	encapsulation: ViewEncapsulation.None,
})
export class NavigationMenuComponent {
	clientPath: string;
	user!: UserTLR;
	isUserResponsableOnAtLeastOneOuvrage: boolean = false;

	private userBaseOptions: { filter: (user: UserTLR) => boolean, entry: MenuEntry }[] = [
		{
			filter: (user) => user.role != 'INT',
			entry: {
				path: "/acces",
				title: "Gestion des accès",
				icon: "fa  fa-key fa-fw",
				label: "Gestion des accès",
			}
		},
		{
			filter: (user) => user.role === 'CCE',
			entry: {
				path: "/acces-tarifs-negatifs",
				title: "Tarif Négatif",
				icon: "fa fa-eur fa-fw",
				label: "Tarif Négatif",
			}
		},
		{
			filter: (user) => user.role == 'EXP' || user.role === 'CCE',
			entry: {
				path: "/memos",
				title: "Mémos",
				icon: "fa fa-calendar fa-fw",
				label: "Mémos",
			}
		},
		{
			filter: (_user) => this.isUserResponsableOnAtLeastOneOuvrage,
			entry: {
				path: "/transfert-responsabilites",
				title: "Transfert de responsabilités",
				icon: "fa  fa-exchange fa-fw",
				label: "Transfert de responsabilités",
			}
		},
		{
			filter: (user) => user.role == 'EXP' || user.role == 'CCE',
			entry: {
				path: "/historique-cac-cc",
				title: "Historique des transferts de resp. CAC / CC",
				icon: "fa  fa-th-list fa-fw",
				label: "Historique des transferts de resp. CAC / CC",
			}
		},
		{
			filter: (user) => user.role != 'INT',
			entry: {
				path: "/connaitre-cac-cc",
				title: "Connaître tous les CAC / CC",
				icon: "fa  fa-users fa-fw",
				label: "Connaître tous les CAC / CC",
			}
		},
		{
			filter: (user) => user.role != 'INT',
			entry: {
				path: "/messages-collationnes",
				title: "Messages collationnés",
				icon: "fa  fa-envelope fa-fw",
				label: "Messages collationnés",
			}
		},
		{
			filter: (user) => user.role == 'EXP' || user.role === 'CCE',
			entry: {
				path: "/entreprises",
				title: "Administration des entreprises",
				icon: "fa fa-registered",
				label: "Entreprises",
			}
		},
		{
			filter: (user) => user.role == 'EXP' || user.role === 'CCE',
			entry: {
				path: "/intervenants",
				title: "Gestion des intervenants",
				icon: "fa fa-key",
				label: "Intervenants",
			}
		},
		{
			filter: (user) => user.role == 'EXP' || user.role === 'CCE',
			entry: {
				path: "/habilitations",
				title: "Administration des habilitations",
				icon: "fa fa-balance-scale",
				label: "Habilitations et Formations",
			}
		},
		{
			filter: (user) => user.role == 'EXP' || user.role === 'CCE',
			entry: {
				path: "/document-securite",
				title: "Documents de sécurité",
				icon: "fa fa-file-text",
				label: "Documents de sécurité",
			}
		},
	];
	private adminBaseOptions: { filter: (user: UserTLR) => boolean, entry: MenuEntry }[]  = [
		{
			filter: (user) => user.profil === 'ADMIN',
			entry: {
				path: "/agences",
				title: "Administration Agences",
				icon: "fa fa fa-university",
				label: "Administration Agences",
			}
		},
		{
			filter: (user) => user.profil === 'ADMIN',
			entry: {
				path: "/antennes",
				title: "Administration Antennes",
				icon: "fa fa-sitemap",
				label: "Administration Antennes",
			}
		},
		{
			filter: (user) => user.profil === 'ADMIN' || user.profil === 'REFERENT',
			entry: {
				path: "/groupements-parcs",
				title: "Administration Groupement Parcs",
				icon: "fa fa-map",
				label: "Administration Groupement Parcs",
			}
		},
		{
			filter: (user) => user.profil === 'ADMIN' || user.profil === 'REFERENT',
			entry: {
				path: "/parcs",
				title: "Administration Parcs",
				icon: "fa-regular fa-map",
				label: "Administration Parcs",
			}
		},
		{
			filter: (user) => user.profil === 'ADMIN' || user.profil === 'REFERENT',
			entry: {
				path: "/sousParcs",
				title: "Administration Sous-parcs",
				icon: "fa fa-map-signs",
				label: "Administration Sous-parcs",
			}
		},
		{
			filter: (user) => user.profil === 'ADMIN' || user.profil === 'REFERENT',
			entry: {
				path: "/ouvrages",
				title: "Administration Ouvrages",
				icon: "fa-solid fa-building",
				label: "Administration Ouvrages",
			}
		},
		{
			filter: (user) => user.profil === 'ADMIN',
			entry: {
				path: "/modelesOuvrage",
				title: "Administration Modèles d'Ouvrage",
				icon: "fa-regular fa-building",
				label: "Administration Modèles d'Ouvrage",
			}
		},
		{
			filter: (user) => user.profil === 'ADMIN',
			entry: {
				path: "/constructeurs",
				title: "Administration Constructeurs",
				icon: "fa fa-industry",
				label: "Administration Constructeurs",
			}
		},
		{
			filter: (user) => user.profil === 'ADMIN' || user.profil === 'REFERENT',
			entry: {
				path: "/users",
				title: "Administration Utilisateurs",
				icon: "fa fa-users",
				label: "Administration Utilisateurs",
			}
		},
		{
			filter: (user) => user.profil === 'ADMIN',
			entry: {
				path: "/modelesPlansMaintenance",
				title: "Administration Modèles de Plans de Maintenance",
				icon: "fa fa-briefcase",
				label: "Administration des modèles de plan d'entretien et de contrôle",
			}
		},
		{
			filter: (user) => user.profil === 'ADMIN',
			entry: {
				path: "/typesMaintenance",
				title: "Administration Actes O&M",
				icon: "fa fa-wrench",
				label: "Administration Actes O&M",
			}
		},
		{
			filter: (user) => user.profil === 'ADMIN' || user.profil === 'REFERENT',
			entry: {
				path: "/dedoublonnage",
				title: "Dédoublonnage des entreprises et chargés d'accès",
				icon: "fa fa-eraser",
				label: "Dédoublonnage des entreprises et chargés d'accès",
			}
		},
		{
			filter: (user) => user.profil === 'ADMIN',
			entry: {
				path: "/parametrage-perimetres",
				title: "Paramétrage des CAC / CC des antennes",
				icon: "fa fa-star",
				label: "Paramétrage des CAC / CC des antennes",
			}
		},
		{
			filter: (user) => user.profil === 'ADMIN',
			entry: {
				path: "/historique-cac-cc",
				title: "Historique des transferts de resp. CAC / CC",
				icon: "fa fa-th-list fa-fw",
				label: "Historique des transferts de resp. CAC / CC",
			}
		},
		{
			filter: (user) => user.profil === 'ADMIN',
			entry: {
				path: "/supervisionInterfaces",
				title: "Supervision des interfaces",
				icon: "fa fa-th-list fa-fw",
				label: "Supervision des interfaces",
			}
		},
		{
			filter: (user) => user.profil === 'ADMIN',
			entry: {
				path: "/supervision-jobs",
				title: "Supervision des batchs",
				icon: "fa fa-th-list fa-fw",
				label: "Supervision des batchs",
			}
		},
	];
	
	userOptions!: MenuEntry[];
	adminOptions!: MenuEntry[];
	
	constructor(
		private router: Router,

		@Inject(UserService) private userService: UserService,
		@Inject(EventBusService) eventBusService: EventBusService
	) {
		this.clientPath = (window as any).clientPath;

		let user$ = userService.getCurrentUserTLR();
		
		let userOptions$ = user$.pipe(
			map(
				(user) => {
					let userOptions = this.userBaseOptions
						.filter($ => $.filter(user))
						.map($ => $.entry)
					;
					return userOptions;
				}
			)
		);
		
		let adminOptions$ = user$.pipe(
			map(
				(user) => {
					let adminOptions = this.adminBaseOptions
						.filter($ => $.filter(user))
						.map($ => $.entry)
					;
					
					return adminOptions;
				}
			)
		);
		
		forkJoin({ user: user$, userOptions: userOptions$, adminOptions: adminOptions$ }).subscribe(
			({user, userOptions, adminOptions}) => {
				this.user = user;
				this.userOptions = userOptions;
				this.adminOptions = adminOptions;
				// calculer ses droits CAC
				this.refreshUserCacFlag();
			}
		)
		
		eventBusService.observe(Events.RefreshUserMenuRequested, () => {
			// on a demandé le raffraîchissement du menu
			if (this.user) {
				this.refreshUserCacFlag();
			}
		});
		
		// Quand l'utilisateur change le hash a partir du navigateur (manuellement ou via un bookmark), un evenement "hashchange" est lance
		// Cela ne se produit pas quand la navigation est faite a partir de menu, en revanche, la navigation n'a pas alors lieu
		// On ajoute donc ici un listener pour forcer la navigation quand l'utilisateur realise ce changement de has
		window.addEventListener(
			"hashchange",
			() => {
				let hash = location.hash;
				if (hash) {
					hash = hash.replace("#", "");
				}
				
				this.router.navigateByUrl(hash);
			},
			false,
		);
	}
	
	@Input() portail!: string;
	
	navigateToAccueil() {
		// La page d'accueil fonctionne actuellement en reexecutant le user_service, donc on applique un rechargement par la navigation du navigateur
		// NB: ceci pourra etre retirer quand l'accueuil aura ete migre et ameliore
		let currentLoc = window.location.href;
		let tokens = currentLoc.split("#");
		window.location.href = tokens[0];
	}
	
	refreshUserCacFlag() {
		let isCac$ = this.userService.isUserCacOnAtLeastOneOuvrage(this.user.id);
		let isCc$ = this.userService.isUserCcOnAtLeastOneOuvrage(this.user.id);
		combineLatest([isCac$, isCc$]).subscribe(
			([isCac, isCc]) => {
				this.isUserResponsableOnAtLeastOneOuvrage = isCac || isCc;
				// re filtrer les items du menu
				this.userOptions = this.userBaseOptions.filter($ => $.filter(this.user)).map($ => $.entry);
			}
		);
	}
}